/**
 * SignUp.js — Email subscription sign-up modal.
 *
 * A modal sign-up form (triggered from the header announcement bar) that posts
 * to the engine sign-up endpoint. Visually and functionally mirrors the
 * per-page newsletter form in CTASection.js (the design source of truth):
 * same field styling, label treatment, "Sign up options" accordion, topic
 * multi-select (chips + dropdown), and yellow submit button — wrapped in the
 * existing `signup_model` modal shell.
 *
 * Props:
 *  - SignUpPopup {boolean}       Whether the modal is visible.
 *  - HideSignUpPopup {Function}  Closes the modal.
 *  - ShowSignUpPopup {Function}  Opens the modal.
 *  - EngineURL {string}          Base URL for the engine API.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from 'react';
import {
    Form,
    Modal,
    Alert
} from 'react-bootstrap';
import axios from 'axios';

// Topic options — identical set/names to CTASection (tfa_19 … tfa_38)
const TOPICS = [
    { label: "Building & Construction",                   name: "tfa_19" },
    { label: "Business and Contracts",                    name: "tfa_20" },
    { label: "Consumer",                                  name: "tfa_21" },
    { label: "Corporate & In-House Counsel",              name: "tfa_22" },
    { label: "Costs",                                     name: "tfa_23" },
    { label: "Criminal",                                  name: "tfa_24" },
    { label: "Employment & Workplace Relations",          name: "tfa_25" },
    { label: "Ethics",                                    name: "tfa_26" },
    { label: "Family",                                    name: "tfa_27" },
    { label: "Finance, Tax, Superannuation",              name: "tfa_28" },
    { label: "Government, Privacy & FOI",                 name: "tfa_29" },
    { label: "Insolvency & Restructuring",                name: "tfa_30" },
    { label: "Intellectual Property",                     name: "tfa_31" },
    { label: "Litigation & Advocacy",                     name: "tfa_32" },
    { label: "Migration",                                 name: "tfa_33" },
    { label: "Personal Injury & Medico-Legal",            name: "tfa_34" },
    { label: "Professional Skills & Practice Management", name: "tfa_35" },
    { label: "Property, Leases & Planning",               name: "tfa_36" },
    { label: "Sports Law",                                name: "tfa_37" },
    { label: "Wills, Trusts & Succession Planning",       name: "tfa_38" },
];

export const SignUp = (props) => {

    const [SuccessMessage, setSuccessMessage] = useState(undefined);
    const [ErrorMessage, setErrorMessage] = useState(undefined);
    const [SomethingUpadating, setSomethingUpadating] = useState(false);

    // Per-field validation errors (Figma 3:30654 — red border + inline message)
    const [firstName_ER, setFirstName_ER] = useState(false);
    const [lastName_ER, setLastName_ER] = useState(false);
    const [email_ER, setEmail_ER] = useState(false);
    const [organisation_ER, setOrganisation_ER] = useState(false);
    const [topics_ER, setTopics_ER] = useState(false);

    // Topic multi-select UI state (mirrors CTASection — UI only, no submit-logic change)
    const [signupOpen, setSignupOpen] = useState(false);
    const [topicsOpen, setTopicsOpen] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const topicsRef = useRef(null);

    // Close topics dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (topicsRef.current && !topicsRef.current.contains(e.target)) {
                setTopicsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /**
     * Reset the form each time the modal closes — clears validation errors,
     * status messages, selected topics, the submit button label, and the form
     * fields — so reopening always starts clean instead of showing the previous
     * values / error messages.
     */
    useEffect(() => {
        if (!props.SignUpPopup) {
            setSuccessMessage(undefined);
            setErrorMessage(undefined);
            setSomethingUpadating(false);
            setFirstName_ER(false);
            setLastName_ER(false);
            setEmail_ER(false);
            setOrganisation_ER(false);
            setTopics_ER(false);
            setSelectedTopics([]);
            setTopicsOpen(false);
            const form = document.getElementById('4951683');
            if (form) form.reset();
        }
    }, [props.SignUpPopup]);

    const toggleTopic = (topic) => {
        setSelectedTopics(prev =>
            prev.includes(topic.name)
                ? prev.filter(n => n !== topic.name)
                : [...prev, topic.name]
        );
        if (topics_ER) setTopics_ER(false);
    };

    const removeTopic = (e, name) => {
        e.stopPropagation();
        setSelectedTopics(prev => prev.filter(n => n !== name));
    };

    const handleSubmit = (event) => {

        event.preventDefault();
        setSuccessMessage(undefined);
        setErrorMessage(undefined);

        // ── Field-level validation (matches the app-wide error pattern) ──
        const form = document.getElementById('4951683');
        const firstName = (form.elements['tfa_2'].value || '').trim();
        const lastName = (form.elements['tfa_3'].value || '').trim();
        const email = (form.elements['tfa_1'].value || '').trim();
        const organisation = (form.elements['tfa_17'].value || '').trim();
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        let valid = true;
        if (!firstName) { setFirstName_ER(true); valid = false; } else { setFirstName_ER(false); }
        if (!lastName) { setLastName_ER(true); valid = false; } else { setLastName_ER(false); }
        if (!email || !emailRe.test(email)) { setEmail_ER(true); valid = false; } else { setEmail_ER(false); }
        if (!organisation) { setOrganisation_ER(true); valid = false; } else { setOrganisation_ER(false); }
        if (selectedTopics.length === 0) { setTopics_ER(true); valid = false; } else { setTopics_ER(false); }

        if (!valid) return;

        setSomethingUpadating(true);
        const finalDate2 = serialize(form);
        axios.post(props.EngineURL + 'signup/index.php', finalDate2, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        })
            .then(function (response) {
                // Removed setTimeout — no benefit, can cause stale state on slow connections
                if (response?.data?.code === 200) {
                    setSuccessMessage(true); // flag only — don't render API HTML into DOM (XSS risk)
                    if (response.data.new_practice_area) {
                        localStorage.setItem("new_practice_area", response.data.new_practice_area);
                    }
                    // Clear form via React state
                    // this resets hidden/uncontrolled fields in production builds
                } else {
                    // Show generic message — don't show raw API message to user
                    if (response.data.message) {
                        setErrorMessage(response.data.message);
                    }else{
                        setErrorMessage("Something went wrong. Please try again.");
                    }
                }
                setSomethingUpadating(false);
            })
            .catch(function (error) {
                // FIXED: removed console.log(error) — never log raw error objects/stack
                // traces to the browser console in production, they can leak internal
                // URLs, request payloads, or backend error messages.
                setSomethingUpadating(false);
                if (error.response) {
                    if (error.response.status === 429) {
                        setErrorMessage("Too many requests. Please wait a moment and try again.");
                    } else if (error.response.status >= 500) {
                        setErrorMessage("A server error occurred. Please try again later.");
                    } else {
                        setErrorMessage("Something went wrong. Please try again.");
                    }
                } else if (error.request) {
                    // FIXED: this branch is "no response received" (network drop, CORS
                    // block, timeout) — not necessarily rate limiting. The real 429
                    // case is already handled above via error.response.status.
                    setErrorMessage("Could not reach the server. Please check your connection and try again.");
                } else {
                    setErrorMessage("Something went wrong. Please try again.");
                }
            });

    }

    const serialize = (form) => {
        var field, s = [];
        if (typeof form === 'object' && form.nodeName === "FORM") {
            var len = form.elements.length;
            for (var i = 0; i < len; i++) {
                field = form.elements[i];
                if (field.name && !field.disabled && field.type !== 'file' && field.type !== 'reset' && field.type !== 'submit' && field.type !== 'button') {
                    if (field.type === 'select-multiple') {
                        for (var j = form.elements[i].options.length - 1; j >= 0; j--) {
                            if (field.options[j].selected)
                                s[s.length] = encodeURIComponent(field.name) + "=" + encodeURIComponent(field.options[j].value);
                        }
                    } else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
                        s[s.length] = encodeURIComponent(field.name) + "=" + encodeURIComponent(field.value);
                    }
                }
            }
        }
        return s.join('&').replace(/%20/g, '+');
    }


    return (
        <>
            <Modal className="signup_model" show={props.SignUpPopup} onHide={props.HideSignUpPopup} scrollable>
                <Modal.Header closeButton className="border-0 pb-0">
                </Modal.Header>
                <Modal.Body className="pt-0 px-4">
                    {SuccessMessage !== undefined ?
                        <h5 className="text-center">Thank you for signing up to our mailing list</h5> :
                        <Modal.Title className="text-center"> Sign Up To Our Mailing List</Modal.Title>}
                    {SuccessMessage === undefined ?
                        <Form className="cta_form" method="post" onSubmit={handleSubmit} id="4951683" role="form" noValidate>

                            {/* Form fields */}
                            <div className="cta_field_group">
                                <div className="cta_field">
                                    <label className="cta_label cta_label_dark">FIRST NAME <span>*</span></label>
                                    <Form.Control
                                        name="tfa_2"
                                        type="text"
                                        placeholder="First name"
                                        className={firstName_ER ? "form-error" : ""}
                                        disabled={SomethingUpadating}
                                        onChange={() => { if (firstName_ER) setFirstName_ER(false); }}
                                        required
                                    />
                                    {firstName_ER && <span className="field-error-msg">First name is required</span>}
                                </div>
                                <div className="cta_field">
                                    <label className="cta_label cta_label_dark">LAST NAME <span>*</span></label>
                                    <Form.Control
                                        name="tfa_3"
                                        type="text"
                                        placeholder="Last name"
                                        className={lastName_ER ? "form-error" : ""}
                                        disabled={SomethingUpadating}
                                        onChange={() => { if (lastName_ER) setLastName_ER(false); }}
                                        required
                                    />
                                    {lastName_ER && <span className="field-error-msg">Last name is required</span>}
                                </div>
                                <div className="cta_field">
                                    <label className="cta_label cta_label_dark">EMAIL <span>*</span></label>
                                    <Form.Control
                                        name="tfa_1"
                                        type="email"
                                        placeholder="Email address"
                                        className={email_ER ? "form-error" : ""}
                                        disabled={SomethingUpadating}
                                        onChange={() => { if (email_ER) setEmail_ER(false); }}
                                        required
                                    />
                                    {email_ER && <span className="field-error-msg">Please enter a valid email address</span>}
                                </div>
                                <div className="cta_field">
                                    <label className="cta_label cta_label_dark">ORGANISATION <span>*</span></label>
                                    <Form.Control
                                        name="tfa_17"
                                        type="text"
                                        placeholder="Organisation"
                                        className={organisation_ER ? "form-error" : ""}
                                        disabled={SomethingUpadating}
                                        onChange={() => { if (organisation_ER) setOrganisation_ER(false); }}
                                        required
                                    />
                                    {organisation_ER && <span className="field-error-msg">Organisation is required</span>}
                                </div>
                            </div>

                            {/* Sign up options — title kept, collapse toggle removed (always open) */}
                            <div className="cta_signup_accordion">
                                <div className="cta_signup_trigger cta_signup_heading">
                                    <span>
                                        Sign up options <span>*</span>
                                        {selectedTopics.length > 0 && ` (${selectedTopics.length})`}
                                    </span>
                                </div>
                                <div className="cta_signup_panel">
                                        {/* Topics multi-select */}
                                        <div className={`cta_topics_field${topics_ER ? ' form-error' : ''}`} ref={topicsRef}>
                                            {/* All practice areas shown as an always-visible checkbox list (no collapse toggle) */}
                                            <div className="cta_topics_dropdown cta_topics_static">
                                                {TOPICS.map(topic => (
                                                    <div
                                                        key={topic.name}
                                                        className={`cta_topics_option${selectedTopics.includes(topic.name) ? ' selected' : ''}`}
                                                        onClick={() => toggleTopic(topic)}
                                                    >
                                                        <span className="cta_topics_checkbox">
                                                            {selectedTopics.includes(topic.name) && (
                                                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M1 4L3.5 6.5L9 1" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            )}
                                                        </span>
                                                        {topic.label}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {topics_ER && <span className="field-error-msg">Please select at least one topic</span>}

                                        {/* Hidden checkboxes for form serialization (tfa_19 … tfa_38) */}
                                        <div style={{ display: 'none' }}>
                                            {TOPICS.map(topic => (
                                                <input
                                                    key={topic.name}
                                                    type="checkbox"
                                                    name={topic.name}
                                                    value={topic.name}
                                                    checked={selectedTopics.includes(topic.name)}
                                                    onChange={() => {}}
                                                />
                                            ))}
                                        </div>
                                    </div>
                            </div>

                            {/* Hidden form fields */}
                            <input type="hidden" value="4951683" name="tfa_dbFormId" id="tfa_dbFormId" />
                            <input type="hidden" value="" name="tfa_dbResponseId" id="tfa_dbResponseId" />
                            <input type="hidden" value="9ddbb774993d2dd282ddaa3326b474d9" name="tfa_dbControl" id="tfa_dbControl" />
                            <input type="hidden" value="" name="tfa_dbWorkflowSessionUuid" id="tfa_dbWorkflowSessionUuid" />
                            <input type="hidden" value="2" name="tfa_dbVersionId" id="tfa_dbVersionId" />
                            <input type="hidden" value="" name="tfa_switchedoff" id="tfa_switchedoff" />

                            {/* Submit */}
                            <button 
                                type="submit" 
                                className="cta_submit_btn btn justify-content-center" 
                                disabled={SomethingUpadating}
                                id="submit_button">
                                {SomethingUpadating ? 'Please wait..' : 'Submit'}
                                {!SomethingUpadating && (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </button>

                            {ErrorMessage !== undefined ?
                                <Alert variant="danger" className="mt-3">
                                    {ErrorMessage}
                                </Alert>
                                : ''}
                        </Form> : null}
                </Modal.Body>
            </Modal>
        </>
    )
}
